<template>
  <div class="bulk-jobs-list">
    <div class="list-header">
      <h3 class="list-title">I tuoi Job di Analisi</h3>
      <button @click="$emit('refresh')" class="refresh-button" :disabled="loading">
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          class="refresh-icon"
          :class="{ spinning: loading }"
        >
          <path d="M23 4v6h-6" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M1 20v-6h6" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </button>
    </div>

    <!-- Empty State -->
    <div v-if="!loading && jobs.length === 0" class="empty-state">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" class="empty-icon">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        <polyline points="14,2 14,8 20,8" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        <line x1="12" y1="18" x2="12" y2="12" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        <line x1="9" y1="15" x2="15" y2="15" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>
      <p class="empty-text">Nessun job di analisi in blocco</p>
      <p class="empty-subtext">Carica un file Excel o CSV per iniziare</p>
    </div>

    <!-- Loading State -->
    <div v-if="loading && jobs.length === 0" class="loading-state">
      <div class="spinner"></div>
      <p>Caricamento job...</p>
    </div>

    <!-- Job Cards -->
    <div v-if="jobs.length > 0" class="jobs-grid">
      <div
        v-for="job in jobs"
        :key="job.job_id"
        class="job-card"
        :class="{ 'job-card--active': isActive(job.status) }"
        @click="$emit('select', job)"
      >
        <div class="job-card-header">
          <h4 class="job-name">{{ job.job_name || job.original_filename }}</h4>
          <span class="status-badge" :class="'status-badge--' + job.status">
            {{ getStatusLabel(job.status) }}
          </span>
        </div>

        <div class="job-card-progress">
          <div class="progress-bar">
            <div
              class="progress-bar-fill"
              :class="'progress-bar-fill--' + job.status"
              :style="{ width: (job.progress_percentage || 0) + '%' }"
            ></div>
          </div>
          <span class="progress-text">
            {{ job.completed_count + job.failed_count }} / {{ job.total_count }}
          </span>
        </div>

        <div class="job-card-meta">
          <span class="meta-item">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" class="meta-icon">
              <rect x="3" y="4" width="18" height="18" rx="2" ry="2" stroke-width="2"/>
              <line x1="16" y1="2" x2="16" y2="6" stroke-width="2" stroke-linecap="round"/>
              <line x1="8" y1="2" x2="8" y2="6" stroke-width="2" stroke-linecap="round"/>
              <line x1="3" y1="10" x2="21" y2="10" stroke-width="2"/>
            </svg>
            {{ formatDate(job.created_at) }}
          </span>
          <span v-if="job.failed_count > 0" class="meta-item meta-item--warning">
            {{ job.failed_count }} fallite
          </span>
        </div>

        <div class="job-card-actions">
          <button
            v-if="job.status === 'completed' || job.status === 'cancelled'"
            @click.stop="$emit('download', job)"
            class="action-btn"
            title="Scarica risultati"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              <polyline points="7,10 12,15 17,10" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              <line x1="12" y1="15" x2="12" y2="3" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </button>
          <button
            @click.stop="$emit('delete', job)"
            class="action-btn action-btn--danger"
            title="Elimina"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <polyline points="3,6 5,6 21,6" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </button>
        </div>
      </div>
    </div>

    <!-- Pagination -->
    <div v-if="totalPages > 1" class="pagination">
      <button
        @click="$emit('page', currentPage - 1)"
        :disabled="currentPage === 1"
        class="pagination-btn"
      >
        &laquo; Precedente
      </button>
      <span class="pagination-info">
        Pagina {{ currentPage }} di {{ totalPages }}
      </span>
      <button
        @click="$emit('page', currentPage + 1)"
        :disabled="currentPage === totalPages"
        class="pagination-btn"
      >
        Successiva &raquo;
      </button>
    </div>
  </div>
</template>

<script>
export default {
  name: 'BulkJobsList',
  props: {
    jobs: {
      type: Array,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
    currentPage: {
      type: Number,
      default: 1,
    },
    totalPages: {
      type: Number,
      default: 1,
    },
  },
  emits: ['select', 'download', 'delete', 'refresh', 'page'],
  methods: {
    getStatusLabel(status) {
      const labels = {
        pending: 'In attesa',
        processing: 'In elaborazione',
        paused: 'In pausa',
        completed: 'Completato',
        cancelled: 'Annullato',
        failed: 'Fallito',
      };
      return labels[status] || status;
    },
    isActive(status) {
      return ['pending', 'processing', 'paused'].includes(status);
    },
    formatDate(dateString) {
      if (!dateString) return '-';
      const date = new Date(dateString);
      return date.toLocaleDateString('it-IT', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    },
  },
};
</script>

<style scoped>
.bulk-jobs-list {
  background: white;
}

.list-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
}

.list-title {
  font-size: 1.25rem;
  font-weight: 600;
  color: #1a1a1a;
  margin: 0;
}

.refresh-button {
  background: none;
  border: none;
  cursor: pointer;
  padding: 0.5rem;
  color: #6b7280;
  transition: color 0.2s ease;
}

.refresh-button:hover {
  color: #2e4eed;
}

.refresh-button:disabled {
  cursor: not-allowed;
  opacity: 0.5;
}

.refresh-icon {
  width: 20px;
  height: 20px;
}

.refresh-icon.spinning {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* Empty State */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 3rem 1rem;
  text-align: center;
}

.empty-icon {
  width: 64px;
  height: 64px;
  color: #d1d5db;
  margin-bottom: 1rem;
}

.empty-text {
  font-size: 1.125rem;
  font-weight: 500;
  color: #6b7280;
  margin: 0 0 0.5rem;
}

.empty-subtext {
  font-size: 0.875rem;
  color: #9ca3af;
  margin: 0;
}

/* Loading State */
.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 2rem;
}

.spinner {
  width: 32px;
  height: 32px;
  border: 3px solid #e5e7eb;
  border-top-color: #2e4eed;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 0.5rem;
}

/* Job Cards */
.jobs-grid {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.job-card {
  padding: 1rem;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.job-card:hover {
  border-color: #2e4eed;
  box-shadow: 0 2px 8px rgba(46, 78, 237, 0.1);
}

.job-card--active {
  border-color: #2e4eed;
  background: #f8fafc;
}

.job-card-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 0.75rem;
}

.job-name {
  font-size: 1rem;
  font-weight: 500;
  color: #1a1a1a;
  margin: 0;
  max-width: 60%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.status-badge {
  padding: 0.25rem 0.5rem;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  border-radius: 4px;
}

.status-badge--pending {
  background: #f3f4f6;
  color: #6b7280;
}

.status-badge--processing {
  background: #dbeafe;
  color: #1d4ed8;
}

.status-badge--paused {
  background: #fef3c7;
  color: #b45309;
}

.status-badge--completed {
  background: #d1fae5;
  color: #059669;
}

.status-badge--cancelled {
  background: #f3f4f6;
  color: #6b7280;
}

.status-badge--failed {
  background: #fee2e2;
  color: #dc2626;
}

.job-card-progress {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 0.75rem;
}

.progress-bar {
  flex: 1;
  height: 6px;
  background: #e5e7eb;
  border-radius: 3px;
  overflow: hidden;
}

.progress-bar-fill {
  height: 100%;
  transition: width 0.3s ease;
}

.progress-bar-fill--pending,
.progress-bar-fill--processing {
  background: #2e4eed;
}

.progress-bar-fill--paused {
  background: #f59e0b;
}

.progress-bar-fill--completed {
  background: #10b981;
}

.progress-bar-fill--cancelled,
.progress-bar-fill--failed {
  background: #6b7280;
}

.progress-text {
  font-size: 0.75rem;
  color: #6b7280;
  min-width: 60px;
  text-align: right;
}

.job-card-meta {
  display: flex;
  gap: 1rem;
  font-size: 0.75rem;
  color: #6b7280;
  margin-bottom: 0.75rem;
}

.meta-item {
  display: flex;
  align-items: center;
  gap: 0.25rem;
}

.meta-item--warning {
  color: #dc2626;
}

.meta-icon {
  width: 14px;
  height: 14px;
}

.job-card-actions {
  display: flex;
  gap: 0.5rem;
  justify-content: flex-end;
}

.action-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.action-btn svg {
  width: 16px;
  height: 16px;
  color: #6b7280;
}

.action-btn:hover {
  border-color: #2e4eed;
}

.action-btn:hover svg {
  color: #2e4eed;
}

.action-btn--danger:hover {
  border-color: #ef4444;
  background: #fee2e2;
}

.action-btn--danger:hover svg {
  color: #dc2626;
}

/* Pagination */
.pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 1rem;
  margin-top: 1.5rem;
  padding-top: 1rem;
  border-top: 1px solid #e5e7eb;
}

.pagination-btn {
  padding: 0.5rem 1rem;
  font-size: 0.875rem;
  background: white;
  border: 1px solid #2e4eed;
  color: #2e4eed;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.pagination-btn:hover:not(:disabled) {
  background: #2e4eed;
  color: white;
}

.pagination-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.pagination-info {
  font-size: 0.875rem;
  color: #6b7280;
}

@media (max-width: 480px) {
  .job-card-header {
    flex-direction: column;
    gap: 0.5rem;
  }

  .job-name {
    max-width: 100%;
  }

  .job-card-meta {
    flex-direction: column;
    gap: 0.25rem;
  }

  .pagination {
    flex-direction: column;
    gap: 0.5rem;
  }
}
</style>
